/**
 * TypeScript types for Observer app
 * Defines interfaces for AI threads, messages, filters, and state management
 */

import { ActionTypes } from '../../store/actionTypes';

export interface AiThreadWithStats {
  ai_thread_id: number;
  user_id: string | null;
  ticket_id: string | null;
  thread_name: string | null;
  pinned: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string | null;
  message_count: number;
  last_message_at: string | null;
}

export interface AiThreadDetail {
  ai_thread_id: number;
  user_id: string | null;
  ticket_id: string | null;
  thread_name: string | null;
  pinned: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface Message {
  message_id: number;
  ai_thread_id: number;
  user_id: string | null;
  content_type: 'MESSAGE' | 'HUMAN_REVIEW';
  content: string;
  created_at: string;
}

export interface ObserverFilters {
  userId: string;
  ticketId: string;
  isDeleted: boolean | null;
  startDate: Date | null;
  endDate: Date | null;
  minMessages: number | null;
  maxMessages: number | null;
}

export interface SortConfig {
  by: 'created' | 'updated' | 'messageCount';
  order: 'ASC' | 'DESC';
}

export interface PaginationConfig {
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface ObserverStats {
  totalThreads: number;
  activeThreads: number;
  deletedThreads: number;
  todayMessages: number;
}

export interface ObserverState {
  threads: AiThreadWithStats[];
  selectedThread: AiThreadDetail | null;
  selectedMessages: Message[];
  filters: ObserverFilters;
  sort: SortConfig;
  pagination: PaginationConfig;
  stats: ObserverStats;
  loading: boolean;
  error: string | null;
  showFilters: boolean;
}

export interface ThreadSearchResult {
  threads: AiThreadWithStats[];
  totalCount: number;
  stats: ObserverStats;
}

export interface UserSearchResult {
  users: string[];
}

// Action types
export const OBSERVER_ACTION_TYPES = {
  SET_LOADING: ActionTypes.OBSERVER_SET_LOADING,
  SET_ERROR: ActionTypes.OBSERVER_SET_ERROR,
  SET_THREADS: ActionTypes.OBSERVER_SET_THREADS,
  SET_SELECTED_THREAD: ActionTypes.OBSERVER_SET_SELECTED_THREAD,
  SET_SELECTED_MESSAGES: ActionTypes.OBSERVER_SET_SELECTED_MESSAGES,
  SET_FILTERS: ActionTypes.OBSERVER_SET_FILTERS,
  SET_SORT: ActionTypes.OBSERVER_SET_SORT,
  SET_PAGINATION: ActionTypes.OBSERVER_SET_PAGINATION,
  SET_STATS: ActionTypes.OBSERVER_SET_STATS,
  TOGGLE_FILTERS: ActionTypes.OBSERVER_TOGGLE_FILTERS,
  RESET_FILTERS: ActionTypes.OBSERVER_RESET_FILTERS,
} as const;

export type ObserverActionType = typeof OBSERVER_ACTION_TYPES[keyof typeof OBSERVER_ACTION_TYPES];

// Action interfaces
export interface SetLoadingAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_LOADING;
  payload: boolean;
}

export interface SetErrorAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_ERROR;
  payload: string | null;
}

export interface SetThreadsAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_THREADS;
  payload: AiThreadWithStats[];
}

export interface SetSelectedThreadAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_SELECTED_THREAD;
  payload: AiThreadDetail | null;
}

export interface SetSelectedMessagesAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_SELECTED_MESSAGES;
  payload: Message[];
}

export interface SetFiltersAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_FILTERS;
  payload: Partial<ObserverFilters>;
}

export interface SetSortAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_SORT;
  payload: SortConfig;
}

export interface SetPaginationAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_PAGINATION;
  payload: Partial<PaginationConfig>;
}

export interface SetStatsAction {
  type: typeof OBSERVER_ACTION_TYPES.SET_STATS;
  payload: ObserverStats;
}

export interface ToggleFiltersAction {
  type: typeof OBSERVER_ACTION_TYPES.TOGGLE_FILTERS;
}

export interface ResetFiltersAction {
  type: typeof OBSERVER_ACTION_TYPES.RESET_FILTERS;
}

export type ObserverAction = 
  | SetLoadingAction
  | SetErrorAction
  | SetThreadsAction
  | SetSelectedThreadAction
  | SetSelectedMessagesAction
  | SetFiltersAction
  | SetSortAction
  | SetPaginationAction
  | SetStatsAction
  | ToggleFiltersAction
  | ResetFiltersAction;
